import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';

import { switchMap } from 'rxjs/operators';

import { TargetingResultModel } from '../../../../../models/targeting-result.model';
import { TargetingSearchService } from '../../../../../services/targeting-search.service';
import { ContactService } from '../../../../../../../Common/TargetingShared/services/contact-service';
import { DiscoverRoutingService } from '../../../../../../DiscoverRouter/services/discover-routing.service';
import { TargetingListService } from '../../../../../services/targeting-list.service';
import { LocalizationService } from '../../../../../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../../../../../Core/Dialogs/services/dialog.service';
import { ContactsPublicApi } from '../../../../../../../api/targeting/contacts-public.api';
import { OutletContactTabType, TargetingRhpContextsService } from '../../../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { EntityTypeDefaultImageEnum } from '../../../../../../../Common/Misc/enums/entity-type-default-image.enum';
import { EmploymentFactory } from '../../../../../factories/employment.factory';
import { promiseOnlyResolved } from '../../../../../../../Core/utils';
import { ResultCardDirective } from '../../../../../../../Common/TargetingShared/components/ResultCard/result-card.directive';
import { ListPickerService } from '../../../../../../../Common/RhpContexts/services/list-picker.service';
import { EntityType } from '../../../../../../../api/targeting/enum/entity.type.enum';
import { RecipientEditorService } from '../../recipient-editor.service';
import { DistributionRecipientModel } from '../../../../../models/distribution-recipient-model';
import { ManageListDialogComponent } from '../../../../TargetingResults/ManageListDialog/manage-list-dialog.component';
import { ButtonTypesEnum } from '../../../../../../../Common/Forms/enums/button-types.enum';
import { RecipientViewerService } from '../recipient-viewer-service';
import { DistributionPickerService } from '@Common/RhpContexts/services/distribution-picker.service';


export enum CardMode {
    searchResults,
    editorMatchedContact
}
@Component({
    selector: 'agpr-recipient-viewer-card',
    templateUrl: './recipient-viewer-card.component.html',
    styleUrls: ['../../../../../../../Common/TargetingShared/components/ResultCard/result-card.scss', './recipient-viewer-card.component.scss'],
    standalone: false
})
export class RecipientViewerCardComponent extends ResultCardDirective implements OnDestroy {

    @Input() cardMode: CardMode = CardMode.searchResults;
    @Input() recipient: DistributionRecipientModel;

    @Output() showContactEditor: EventEmitter<void> = new EventEmitter<void>();
    @Output() showRhpTakeOver?: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();
    @Output() focus: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();
    @Output() expand: EventEmitter<TargetingResultModel> = new EventEmitter<TargetingResultModel>();

    readonly cardModes = CardMode;
    selectedContactId: string;
    imageDefault = EntityTypeDefaultImageEnum;
    excluded: boolean = false;

    readonly entityTypes = EntityType;
    exclusions: any[];

    readonly buttonTypes = ButtonTypesEnum;

    // Override to call setExclusionFlag
    @Input() set entity(value: TargetingResultModel) {
        this.entityInternal = value;
        this.updateListMembershipInfo();
        this.setDisplayedReachValue();
        this.setExclusionFlag();
    }

    constructor(
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public listPickerService: ListPickerService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        public contactService: ContactService,
        public discoverRoutingService: DiscoverRoutingService,
        public contactsPublicApi: ContactsPublicApi,
        public targetingRhpContextsService: TargetingRhpContextsService,
        private employmentFactory: EmploymentFactory,
        private recipientEditorService: RecipientEditorService,
        private recipientViewerService: RecipientViewerService,
        public distributionPickerService: DistributionPickerService,
    ) {
        super(targetingSearchService,
            targetingListService,
            listPickerService,
            localizationService,
            coreDialogService,
            targetingRhpContextsService,
            distributionPickerService);
        this.subscriptions.push(
            this.targetingRhpContextsService.getContactId().subscribe(contactId => {
                this.selectedContactId = contactId;
            }),
            this.targetingSearchService.rhpSelectedEmployment().subscribe(res => {
                if (res) {
                    this.rhpSelectedEmploymentChanged();
                }
            }),
            this.recipientEditorService.getExcludedRecipients().subscribe(excludedRecipients => {
                this.exclusions = excludedRecipients;
                this.setExclusionFlag();
            })
        );
    }
    setExclusionFlag() {
        this.excluded = false;

        if (this.entityInternal && this.exclusions) {
            if (this.entityInternal.entityType === EntityType.OUTLET &&
                this.exclusions.find(x => x.outletId === this.entityInternal.outletModel.id)) {
                this.excluded = true;
            } else if (this.entityInternal.entityType === EntityType.CONTACT &&
                this.exclusions.find(x => x.contactId === this.entityInternal.contactModel.id)) {
                this.excluded = true;
            }
        }
    }
    editContact(item: TargetingResultModel) {
        this.contactService.setEditingContact(item.id);
        // TODO : Ideally we should update the URL but not lose the "context"
        // so that the user can share the page but still be able to close the overlay and return
        // to his original context
        this.showContactEditor.emit();
    }

    deleteContact(item: TargetingResultModel) {
        this.contactService.deleteContacts([item]);
    }

    focusOutlet() {
        // TODO : Maybe this should pop a takeover instead of selecting
        this.targetingRhpContextsService.setEntityByResult(this.entityInternal);
        this.targetingRhpContextsService.selectOutletContactTab(OutletContactTabType.outletProfile);
    }

    selectedEmploymentChanged() {
        if (this.selectedContactId === this.entityInternal.id) {
            this.targetingRhpContextsService.changeSelectedEmployment(this.entityInternal.selectedEmployment.id);
        }
    }

    toggleExpansion() {
        if (this.expanded) {
            this.expanded = false;
        } else {
            if (!this.entityInternal.loadedEmployments) {
                this.contactsPublicApi.getContact(this.entityInternal.id).pipe(
                    switchMap(contact => {
                        const employmentPromises = contact.employments.map(x => this.employmentFactory.create(x));
                        return promiseOnlyResolved(employmentPromises);
                    }),
                ).subscribe(employments => {
                    this.entityInternal.loadedEmployments = employments;
                    // Update the ref so the radio button behaves
                    this.entityInternal.selectedEmployment = this.entityInternal.loadedEmployments
                        .find(x => x.id === this.entityInternal.selectedEmployment.id);
                    this.expanded = true;
                });
            } else {
                this.expanded = true;
            }
        }
    }

    rhpSelectedEmploymentChanged(): void {
        if (this.expanded) {
            this.entityInternal.selectedEmployment = this.entityInternal.loadedEmployments
                .find(x => x.id === this.entityInternal.selectedEmployment.id);
        }
    }

    cardFocused() {
        this.focus.emit(this.entityInternal);
    }

    expandClicked() {
        this.expand.emit(this.entityInternal);
    }

    includeRecipient() {
        this.recipientViewerService.removeExclusion(this.recipient, this.entityInternal);
    }

    excludeRecipient() {
        this.recipientViewerService.addExclusion(this.recipient, this.entityInternal);
    }

    openManageListDialog(entity: TargetingResultModel) {
        this.coreDialogService.open(ManageListDialogComponent, {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        });
    }
}
